import { useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { Option, RenderIf } from 'react-rainbow-components';
import Badge from 'components/Badge';
import { formatRelativeDate } from 'data/services/date/formatter';
import { useNexhealthAppointments, useNexhealthLocation } from '../hooks';
import { NexhealthAppointment } from '../types';
import {
    AppointmentDetails,
    AppointmentInfo,
    AppointmentItem,
    AppointmentsList,
    AppointmentStatuses,
    AppointmentTitle,
    DateAvatar,
    DateDay,
    DateMonth,
    NextAppointmentItem,
    RangeFilter,
    RangeLabel,
    RangePicklist,
    Section,
    SectionHeader,
    SectionTitle,
    SkeletonDate,
    SkeletonLine,
    StyledEmptyMessage,
} from './styled';

const rangeOptions = [
    { name: '3', label: 'Last 3 months', value: 3 },
    { name: '6', label: 'Last 6 months', value: 6 },
    { name: '12', label: 'Last 12 months', value: 12 },
];

const Appointment = ({
    appointment,
    locationName,
    isUpcoming,
    isNext,
    minimal = false,
}: {
    appointment: NexhealthAppointment;
    locationName?: string;
    isUpcoming?: boolean;
    isNext?: boolean;
    minimal?: boolean;
}) => {
    const Item = isNext ? NextAppointmentItem : AppointmentItem;
    const start = DateTime.fromISO(appointment.start_time);
    const end = appointment.end_time ? DateTime.fromISO(appointment.end_time) : undefined;
    const time = [
        start.toLocaleString(DateTime.TIME_SIMPLE),
        end?.isValid && end.toLocaleString(DateTime.TIME_SIMPLE),
    ].filter(Boolean).join(' – ');

    const details = [
        formatRelativeDate(appointment.start_time),
        appointment.provider_name,
        locationName,
    ].filter(Boolean).join(' · ');

    const sameMeridiem = end?.isValid && end.toFormat('a') === start.toFormat('a');
    const compactTime = end?.isValid
        ? `${start.toFormat(sameMeridiem ? 'h:mm' : 'h:mm a')} – ${end.toFormat('h:mm a')}`
        : start.toFormat('h:mm a');
    let dayPart = 'evening';
    if (start.hour < 12) dayPart = 'morning';
    else if (start.hour < 17) dayPart = 'afternoon';
    const dayDiff = Math.round(
        start.startOf('day').diff(DateTime.now().startOf('day'), 'days').days,
    );
    let relativeDay = formatRelativeDate(appointment.start_time);
    if (dayDiff === 0) relativeDay = `This ${dayPart}`;
    else if (dayDiff === 1) relativeDay = `Tomorrow ${dayPart}`;
    else if (dayDiff === -1) relativeDay = `Yesterday ${dayPart}`;
    const compactDetails = `${start.toFormat('ccc')} · ${relativeDay}`;

    return (
        <Item>
            <DateAvatar>
                <DateMonth>{start.toFormat('LLL')}</DateMonth>
                <DateDay>{start.toFormat('d')}</DateDay>
            </DateAvatar>
            <AppointmentInfo>
                <AppointmentTitle>
                    {minimal ? compactTime : `${start.toFormat('cccc')}, ${time}`}
                </AppointmentTitle>
                <RenderIf isTrue={minimal}>
                    <AppointmentDetails>{compactDetails}</AppointmentDetails>
                </RenderIf>
                <RenderIf isTrue={!minimal}>
                    <AppointmentDetails>{details}</AppointmentDetails>
                    <RenderIf isTrue={appointment.note}>
                        <AppointmentDetails>{appointment.note}</AppointmentDetails>
                    </RenderIf>
                </RenderIf>
            </AppointmentInfo>
            <AppointmentStatuses>
                <RenderIf isTrue={appointment.cancelled}>
                    <Badge label="Cancelled" variant="error" />
                </RenderIf>
                <RenderIf isTrue={!appointment.cancelled && appointment.patient_missing}>
                    <Badge label="No show" variant="warning" />
                </RenderIf>
                <RenderIf isTrue={!appointment.cancelled && appointment.checkin_at}>
                    <Badge label="Checked in" variant="brand" />
                </RenderIf>
                <RenderIf isTrue={!appointment.cancelled && appointment.confirmed}>
                    <Badge label="Confirmed" variant="success" />
                </RenderIf>
                <RenderIf isTrue={isUpcoming && !appointment.cancelled && !appointment.confirmed}>
                    <Badge label="Unconfirmed" variant="default" />
                </RenderIf>
            </AppointmentStatuses>
        </Item>
    );
};

const AppointmentSkeletons = ({ count }: { count: number }) => (
    <>
        {Array.from({ length: count }, (_, index) => (
            <AppointmentItem key={index}>
                <SkeletonDate shape="square" width="3rem" height="3rem" />
                <AppointmentInfo>
                    <SkeletonLine shape="rounded-rect" width="45%" height="0.875rem" />
                    <SkeletonLine shape="rounded-rect" width="65%" height="0.75rem" />
                </AppointmentInfo>
            </AppointmentItem>
        ))}
    </>
);

const Appointments = ({
    agentId,
    customerId,
    showRangeFilter = true,
    minimal = false,
}: {
    agentId: string;
    customerId: string;
    showRangeFilter?: boolean;
    minimal?: boolean;
}) => {
    const [range, setRange] = useState(rangeOptions[0]);
    const { data, isLoading, isPreviousData } = useNexhealthAppointments({
        agentId,
        customerId,
        months: range.value,
        enabled: true,
    });

    const { upcoming, past } = useMemo(() => {
        const now = Date.now();
        const appointments = data?.data || [];
        return {
            upcoming: appointments
                .filter((appointment) => new Date(appointment.start_time).getTime() >= now)
                .sort((a, b) => a.start_time.localeCompare(b.start_time)),
            past: appointments
                .filter((appointment) => new Date(appointment.start_time).getTime() < now)
                .sort((a, b) => b.start_time.localeCompare(a.start_time)),
        };
    }, [data]);

    const isLoadingPast = isLoading || isPreviousData;
    const nextAppointmentId = upcoming.find((appointment) => !appointment.cancelled)?.id;
    const locationId = data?.data?.[0]?.location_id;
    const { data: location } = useNexhealthLocation(agentId, locationId, !minimal);
    const locationName = location?.data?.name;

    return (
        <>
            <Section>
                <SectionHeader>
                    <SectionTitle>Upcoming Appointments</SectionTitle>
                </SectionHeader>
                <AppointmentsList>
                    <RenderIf isTrue={isLoading}>
                        <AppointmentSkeletons count={2} />
                    </RenderIf>
                    <RenderIf isTrue={!isLoading && upcoming.length === 0}>
                        <StyledEmptyMessage message="No upcoming appointments" />
                    </RenderIf>
                    {!isLoading && upcoming.map((appointment) => (
                        <Appointment
                            key={appointment.id}
                            appointment={appointment}
                            locationName={locationName}
                            isUpcoming
                            isNext={appointment.id === nextAppointmentId}
                            minimal={minimal}
                        />
                    ))}
                </AppointmentsList>
            </Section>
            <Section>
                <SectionHeader>
                    <SectionTitle>Past Appointments</SectionTitle>
                    <RenderIf isTrue={showRangeFilter}>
                        <RangeFilter>
                            <RangeLabel>Showing:</RangeLabel>
                            <RangePicklist
                                value={range}
                                onChange={(value) => {
                                    const option = rangeOptions.find(
                                        (item) => item.value === value.value,
                                    );
                                    if (option) setRange(option);
                                }}
                                borderRadius="semi-rounded"
                                size="small"
                            >
                                {rangeOptions.map((option) => (
                                    <Option key={option.name} {...option} />
                                ))}
                            </RangePicklist>
                        </RangeFilter>
                    </RenderIf>
                </SectionHeader>
                <AppointmentsList>
                    <RenderIf isTrue={isLoadingPast}>
                        <AppointmentSkeletons count={3} />
                    </RenderIf>
                    <RenderIf isTrue={!isLoadingPast && past.length === 0}>
                        <StyledEmptyMessage
                            message={`No appointments in the ${range.label.toLowerCase()}`}
                        />
                    </RenderIf>
                    {!isLoadingPast && past.map((appointment) => (
                        <Appointment
                            key={appointment.id}
                            appointment={appointment}
                            locationName={locationName}
                            minimal={minimal}
                        />
                    ))}
                </AppointmentsList>
            </Section>
        </>
    );
};

export default Appointments;
